import * as React from "react";

import {
  GenericFormConsumerInterface,
  GenericReactProviderFormInterfaceOutput,
  useForm,
  useProminentError,
} from "../../../Common";

import { ContentRoutes, useContentRoute } from "../content-route-provider";

import { useReactGiftCardInitialState } from "../react-gift-card-initial-state-provider";

import {
  AccessForm,
  AccessFormApp,
  AccessFormLogic,
  initialAccessForm,
} from "./access-form";

interface BalanceDetails {
  current_balance: number;
  expiry: string;
  error?: {
    type: string;
    title: string;
  };
}

interface GiftcardBalanceConsumerInterface
  extends GenericFormConsumerInterface<AccessForm> {
  accessForm: AccessForm;

  isLoadingBalance: boolean;
  balanceDetails: BalanceDetails | null;

  onCheckYourBalance: () => void;

  onCheckAnother: () => void;
}

const defaultValue = {
  accessForm: initialAccessForm(),
  balanceDetails: null,
};

const GiftcardBalanceContext = React.createContext(
  defaultValue as unknown as GiftcardBalanceConsumerInterface
);

export const GiftcardBalanceProvider: React.FunctionComponent = (props) => {
  const { initialState } = useReactGiftCardInitialState();
  const { selectedContentRoute } = useContentRoute();
  const { setProminentError } = useProminentError();

  const appContext = React.useMemo(
    () => ({
      initialState,
    }),
    [initialState]
  );
  const AppContext = React.useCallback(() => appContext, [appContext]);
  const thisForm = useForm(AccessFormLogic, AppContext, initialAccessForm);
  const {
    form: accessForm,
    validateRightNowAndGiveMeAYesOrNo,
    resetForm,
  } = thisForm;

  const [isLoadingBalance, setIsLoadingBalance] = React.useState(false);

  const [balanceDetails, setBalanceDetails] =
    React.useState<BalanceDetails | null>(null);

  React.useEffect(() => {
    if (selectedContentRoute !== ContentRoutes.CheckBalance) {
      onCheckAnother();
    }
  }, [selectedContentRoute]);

  const onCheckYourBalance = () => {
    if (validateRightNowAndGiveMeAYesOrNo()) {
      checkYourBalance();
    } else {
      setProminentError(
        "Some information is missing or invalid. Check the red fields above for details."
      );
    }
  };

  const checkYourBalance = async () => {
    try {
      setIsLoadingBalance(true);
      setBalanceDetails(null);

      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Store", initialState.store_code);

      if (initialState.giftcard_balance_recaptcha.recaptchaEnabled) {
        const captchaWrapperId =
          initialState.giftcard_balance_recaptcha.recaptchaId +
          "-react-wrapper";
        let recaptchaInput = document.querySelector(
          ".content.check-balance-page .access-form .input-container.recaptcha-input.named-recaptcha .g-recaptcha-response"
        ) as HTMLTextAreaElement;
        recaptchaInput =
          recaptchaInput ||
          document
            .querySelector("#" + captchaWrapperId)
            ?.querySelector(".g-recaptcha-response");

        if (recaptchaInput) {
          headers.append("X-ReCaptcha", recaptchaInput.value);
        }
      }

      const giftcardBalanceQuery = `mutation {
        bbntGiftcardBalance(
          giftcard_number: "${accessForm.giftcardNumber}"
          giftcard_pin: "${accessForm.giftcardPin}"
        ) {
          current_balance
          expiry
          error {
            title
          }
        }
      }`;

      const request = new Request("/graphql", {
        method: "POST",
        headers,
        body: JSON.stringify({ query: giftcardBalanceQuery }),
      });

      const giftcardBalanceResponse = await fetch(request);

      const giftcardBalanceResponseJson = await giftcardBalanceResponse?.json();

      if (giftcardBalanceResponseJson?.errors) {
        let errorMessage = "An unknown error has occurred.";

        if (Array.isArray(giftcardBalanceResponseJson?.errors)) {
          const firstError = giftcardBalanceResponseJson.errors[0];
          if (firstError) {
            errorMessage = firstError.message;
          }
        }

        throw new Error(errorMessage);
      }

      if (giftcardBalanceResponseJson?.data?.bbntGiftcardBalance) {
        const error =
          giftcardBalanceResponseJson?.data?.bbntGiftcardBalance?.error;
        if (error) {
          throw new Error(error.title);
        }
        setBalanceDetails(giftcardBalanceResponseJson.data.bbntGiftcardBalance);
      }

      setIsLoadingBalance(false);
    } catch (error) {
      console.error(error);
      setIsLoadingBalance(false);

      let errorMessage = "An unknown error has occurred.";
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }

      setProminentError(errorMessage);
    }
  };

  const onCheckAnother = () => {
    resetForm();
    setBalanceDetails(null);
  };

  return (
    <GiftcardBalanceContext.Provider
      value={{
        ...GenericReactProviderFormInterfaceOutput<AccessForm, AccessFormApp>(
          thisForm
        ),

        accessForm,

        onCheckYourBalance,
        onCheckAnother,

        isLoadingBalance,
        balanceDetails,
      }}
    >
      {props.children}
    </GiftcardBalanceContext.Provider>
  );
};

export const useGiftcardBalance = () =>
  React.useContext(GiftcardBalanceContext);
